import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import "./../pages/Services.css";

// ---------------------- Data ----------------------
const projectDefs = [
  // Websites (images)
  { img: require("../images/lasvegas.png"), id: "lasvegas", type: "website", link: "https://lasvegasroofing.com" },
  { img: require("../images/elite.png"),    id: "elite",    type: "website", link: "https://elitebusiness.com" },
  { img: require("../images/jill.png"),     id: "jill",     type: "website", link: "https://techstartup.com" },
  { img: require("../images/tgn.png"),      id: "tgn",      type: "website", link: "https://ecommerce.com" },
  { img: require("../images/embassy.png"),  id: "embassy",  type: "website", link: "https://financefirm.com" },
  { img: require("../images/easyrn.png"),   id: "easyrn",   type: "website", link: "https://ngo.com" },
  { img: require("../images/eqo.png"),      id: "eqo",      type: "website", link: "https://consulting.com" },
  { img: require("../images/halston.png"),  id: "halston",  type: "website", link: "https://realestate.com" },

  // Mobile apps (videos with poster)
  {
    id: "traady",
    type: "mobile",
    link: "https://traadyapp.com",
    video: "https://res.cloudinary.com/dc0g8yyux/video/upload/v1761159809/App2_fb1r6a.mp4",
    img: require("../images/Traadylogin.png"),
  },
  {
    id: "sendnow",
    type: "mobile",
    link: "https://sendnow.com",
    video: "https://res.cloudinary.com/dc0g8yyux/video/upload/v1761160248/App1_y1vkrr.mp4",
    img: require("../images/Sendnow.png"),
  },
];

// ---------------------- Component ----------------------
const Projects = ({ type }) => {
  const { t } = useTranslation();

  const projects = useMemo(
    () =>
      projectDefs.map((p) => ({
        ...p,
        title: t(`projects.${p.id}.title`),
        description: t(`projects.${p.id}.desc`),
      })),
    [t]
  );

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const filteredProjects = projects.filter((p) => p.type === type);

  // Auto-slide only for MOBILE view of the WEBSITES section
  const intervalRef = useRef(null);
  useEffect(() => {
    const shouldAutoSlide =
      isMobile && type !== "mobile" && filteredProjects.length > 0;
    if (!shouldAutoSlide) return;
    intervalRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % filteredProjects.length);
    }, 3500);
    return () => clearInterval(intervalRef.current);
  }, [isMobile, type, filteredProjects.length]);

  const pause = () => intervalRef.current && clearInterval(intervalRef.current);
  const resume = () => {
    const shouldAutoSlide =
      isMobile && type !== "mobile" && filteredProjects.length > 0;
    if (!shouldAutoSlide) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % filteredProjects.length);
    }, 3500);
  };

  return (
    <div
      className={`projects-container ${
        type === "mobile" ? "projects-container--apps" : "projects-container--web"
      }`}
    >
      {isMobile ? (
        // -------------------- MOBILE --------------------
        type === "mobile" ? (
          // Mobile apps: stacked list
          <div className="mobile-list">
            {filteredProjects.map((project, i) => (
              <motion.div
                className="mobile-card"
                key={`${project.id}-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                viewport={{ once: true, amount: 0.2 }}
              >
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-link"
                >
                  <div className="phone-frame">
                    {project.video ? (
                      <video
                        className="project-media"
                        src={project.video}
                        poster={project.img}
                        controls
                        muted
                        playsInline
                        preload="metadata"
                        loop
                      />
                    ) : (
                      <img
                        src={project.img}
                        alt={project.title}
                        className="project-media"
                      />
                    )}
                  </div>
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  <span className="view-link">{t("projects.view_project")}</span>
                </a>
              </motion.div>
            ))}
          </div>
        ) : (
          // Mobile websites: single-card carousel
          filteredProjects.length > 0 && (
            <div className="carousel">
              {(() => {
                const current = filteredProjects[index] || filteredProjects[0];
                if (!current) return null;
                return (
                  <motion.div
                    className="carousel-item"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                    key={`${current.title}-${index}`}
                    onTouchStart={pause}
                    onTouchEnd={resume}
                  >
                    <a
                      href={current.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-link"
                    >
                      <div className="phone-frame">
                        {current.video ? (
                          <video
                            className="project-media"
                            src={current.video}
                            poster={current.img}
                            controls
                            muted
                            playsInline
                            preload="metadata"
                            loop
                          />
                        ) : (
                          <img
                            src={current.img}
                            alt={current.title}
                            className="project-media"
                          />
                        )}
                      </div>
                      <h3>{current.title}</h3>
                      <p>{current.description}</p>
                      <span className="view-link">{t("projects.view_project")}</span>
                    </a>
                  </motion.div>
                );
              })()}
            </div>
          )
        )
      ) : (
        // -------------------- DESKTOP --------------------
        <div className={`grid-container ${type}-grid`}>
          {filteredProjects.map((project, i) => (
            <motion.div
              className="project-box"
              key={`${project.link}-${i}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: i * 0.2 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                <div className="phone-frame">
                  {project.video ? (
                    <video
                      className="project-media"
                      src={project.video}
                      poster={project.img}
                      autoPlay
                      muted
                      playsInline
                      preload="metadata"
                      loop
                    />
                  ) : (
                    <img
                      src={project.img}
                      alt={project.title}
                      className="project-media"
                    />
                  )}
                </div>
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <span className="view-link">{t("projects.view_project")}</span>
              </a>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Projects;